"use client";

/**
 * First-party web analytics beacon (ENG-4281).
 *
 * Replaces `@vercel/analytics` on all three sites: it writes both the
 * automatic pageview and the explicit conversions that used to ride Vercel's
 * `track()`. Everything it sends goes to `/api/ingest/web-events` on
 * platform-app, which owns validation, bot filtering, device enrichment, and
 * the `site` / `environment` / `user_id` derivations.
 *
 * Consent gates it exactly as it gated Vercel Analytics: nothing is sent
 * before `measurement` consent, and withdrawing consent stops future capture.
 * Unlike the Vercel SDK there is no injected script to keep alive afterwards,
 * so the gate is a plain conditional rather than a `beforeSend` guard.
 *
 * Accepted loss (recorded on ENG-4281): no batching and no retry. A beacon
 * that fails is a lost pageview.
 */

import { usePathname } from "next/navigation";
import { Suspense, useEffect } from "react";
import { useHasTelemetryConsent } from "./consent/use-telemetry-consent";
import {
  type AnalyticsEventProperties,
  validateAnalyticsEventProperties,
  type WebConversionEventName,
} from "./event-catalog";
import { ensureFirstTouchCookie } from "./first-touch";

export const WEB_EVENTS_PATH = "/api/ingest/web-events";

const DEFAULT_ROOT_DOMAIN = "514.ax";
const LOCAL_PLATFORM_APP_PORT = "3001";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

type WebEvent = Record<string, unknown> & { event_id: string; type: "pageview" | "conversion" };

export interface WebAnalyticsBeaconProps {
  /**
   * Set on platform-app, which serves the ingest route itself. A relative URL
   * keeps the request same-origin so the Clerk session cookie rides along and
   * the route can stamp `user_id`; it also keeps previews posting to their own
   * deployment instead of production.
   */
  sameOrigin?: boolean;
}

/**
 * Resolves where the beacon posts. Marketing and docs are on different hosts
 * than the ingest route, so they post cross-origin and the route answers with
 * CORS headers. Going direct rather than proxying through a rewrite is
 * deliberate: a rewrite would present Vercel's own infrastructure as the
 * client, and the `/api/ingest/` edge rate-limit rule (ENG-4261) keys on IP.
 */
export function resolveWebEventsEndpoint(sameOrigin = false): string {
  const configured = process.env.NEXT_PUBLIC_WEB_EVENTS_ENDPOINT?.trim();
  if (configured) return configured;
  if (sameOrigin) return WEB_EVENTS_PATH;

  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//${hostname}:${LOCAL_PLATFORM_APP_PORT}${WEB_EVENTS_PATH}`;
  }

  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN?.trim() || DEFAULT_ROOT_DOMAIN;
  return `https://app.${rootDomain}${WEB_EVENTS_PATH}`;
}

/**
 * Fire-and-forget delivery. `sendBeacon` survives the page unload that ends
 * most sessions; the `fetch` fallback keeps the same shape where it is
 * unavailable. Both send `text/plain` so the request stays CORS-simple and
 * never costs a preflight round trip.
 */
function sendWebEvent(event: WebEvent, endpoint: string): void {
  const body = JSON.stringify({ events: [event] });

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
    if (navigator.sendBeacon(endpoint, blob)) return;
  }

  void fetch(endpoint, {
    method: "POST",
    keepalive: true,
    headers: { "content-type": "text/plain;charset=UTF-8" },
    body,
  }).catch(() => {
    // A lost beacon is an accepted loss; it must never surface to the visitor.
  });
}

/**
 * Records a conversion. Replaces `track()` from `@vercel/analytics`, which
 * carried the same `cid` from the ax_ft first-touch cookie.
 */
export function trackWebConversion<Name extends WebConversionEventName>(
  eventName: Name,
  hasTelemetryConsent: boolean,
  properties?: AnalyticsEventProperties<Name>,
  options: { sameOrigin?: boolean } = {},
): void {
  if (!hasTelemetryConsent || typeof window === "undefined") return;

  try {
    validateAnalyticsEventProperties(eventName, properties ?? {});
  } catch {
    console.error("Telemetry capture validation failed", { eventName });
    return;
  }
  const firstTouch = ensureFirstTouchCookie(hasTelemetryConsent);
  sendWebEvent(
    {
      event_id: crypto.randomUUID(),
      type: "conversion",
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      event_name: eventName,
      cid: firstTouch?.cid ?? null,
      ...(properties === undefined ? {} : { properties }),
    },
    resolveWebEventsEndpoint(options.sameOrigin),
  );
}

export function WebAnalyticsBeacon({ sameOrigin }: WebAnalyticsBeaconProps) {
  return (
    <Suspense fallback={null}>
      <PageviewBeacon sameOrigin={sameOrigin} />
    </Suspense>
  );
}

function PageviewBeacon({ sameOrigin }: WebAnalyticsBeaconProps) {
  const hasTelemetryConsent = useHasTelemetryConsent();
  const pathname = usePathname();

  useEffect(() => {
    // Re-runs on withdrawal and returns, so no further pageview is captured.
    if (!hasTelemetryConsent) return;

    const firstTouch = ensureFirstTouchCookie(hasTelemetryConsent);
    // Only the five UTM keys, never the whole query string: page URLs carry
    // invite tokens and one-time links.
    const searchParams = new URLSearchParams(window.location.search);
    const utm = Object.fromEntries(
      UTM_KEYS.map((key) => [key, searchParams.get(key)]).filter(([, value]) => value !== null),
    );

    sendWebEvent(
      {
        event_id: crypto.randomUUID(),
        type: "pageview",
        timestamp: new Date().toISOString(),
        path: pathname,
        referrer: document.referrer || null,
        cid: firstTouch?.cid ?? null,
        ...utm,
      },
      resolveWebEventsEndpoint(sameOrigin),
    );
  }, [hasTelemetryConsent, pathname, sameOrigin]);

  return null;
}
